/**
 * Centralized logging utility for the Smart BI application
 * Provides environment-aware logging with different levels and structured output
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  data?: any;
  error?: Error;
}

class Logger {
  private static instance: Logger;
  private currentLogLevel: LogLevel;
  private isDevelopment: boolean;
  private logHistory: LogEntry[] = [];
  private maxHistorySize = 1000;

  private constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    // Set log level based on environment
    this.currentLogLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Set the minimum log level for output
   */
  setLogLevel(level: LogLevel): void {
    this.currentLogLevel = level;
  }

  /**
   * Get current log level
   */
  getLogLevel(): LogLevel {
    return this.currentLogLevel;
  }

  /**
   * Get log history (useful for debugging)
   */
  getHistory(): LogEntry[] {
    return [...this.logHistory];
  }

  /**
   * Clear log history
   */
  clearHistory(): void {
    this.logHistory = [];
  }

  /**
   * Internal logging method
   */
  private log(level: LogLevel, message: string, context?: string, data?: any, error?: Error): void {
    if (level < this.currentLogLevel) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      context,
      data,
      error
    };

    // Add to history
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    // Format the log message
    const timestamp = entry.timestamp.toISOString();
    const levelName = LogLevel[level];
    const contextStr = context ? `[${context}]` : '';
    const prefix = `[${timestamp}] [${levelName}]${contextStr}`;

    // Output to console only in development or for errors
    if (this.isDevelopment || level >= LogLevel.ERROR) {
      switch (level) {
        case LogLevel.DEBUG:
          console.log(prefix, message, data || '');
          break;
        case LogLevel.INFO:
          console.info(prefix, message, data || '');
          break;
        case LogLevel.WARN:
          console.warn(prefix, message, data || '');
          break;
        case LogLevel.ERROR:
          if (error) {
            console.error(prefix, message, error, data || '');
          } else {
            console.error(prefix, message, data || '');
          }
          break;
      }
    }

    // In production, you might want to send errors to an external service
    if (!this.isDevelopment && level === LogLevel.ERROR) {
      this.sendToErrorReporting(entry);
    }
  }

  /**
   * Send errors to external error reporting service (e.g., Sentry, LogRocket)
   */
  private sendToErrorReporting(entry: LogEntry): void {
    // This is where you would integrate with services like Sentry
    // For now, we'll just store it locally
    try {
      const errorLogs = localStorage.getItem('smartbi_error_logs');
      const logs = errorLogs ? JSON.parse(errorLogs) : [];
      logs.push({
        ...entry,
        timestamp: entry.timestamp.toISOString()
      });
      // Keep only last 50 errors in localStorage
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50);
      }
      localStorage.setItem('smartbi_error_logs', JSON.stringify(logs));
    } catch (e) {
      // Fail silently if localStorage is not available
    }
  }

  /**
   * Debug level logging
   */
  debug(message: string, context?: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, context, data);
  }

  /**
   * Info level logging
   */
  info(message: string, context?: string, data?: any): void {
    this.log(LogLevel.INFO, message, context, data);
  }

  /**
   * Warning level logging
   */
  warn(message: string, context?: string, data?: any): void {
    this.log(LogLevel.WARN, message, context, data);
  }

  /**
   * Error level logging
   */
  error(message: string, context?: string, error?: Error | any, data?: any): void {
    if (error instanceof Error) {
      this.log(LogLevel.ERROR, message, context, data, error);
    } else {
      this.log(LogLevel.ERROR, message, context, { error, ...data });
    }
  }

  /**
   * Log API requests (useful for debugging)
   */
  logApiRequest(method: string, url: string, data?: any, headers?: any): void {
    if (this.isDevelopment) {
      this.debug(`API Request: ${method} ${url}`, 'API', { data, headers });
    }
  }

  /**
   * Log API responses
   */
  logApiResponse(method: string, url: string, status: number, data?: any): void {
    if (this.isDevelopment) {
      this.debug(`API Response: ${method} ${url} (${status})`, 'API', data);
    }
  }

  /**
   * Log API errors
   */
  logApiError(method: string, url: string, error: any): void {
    this.error(`API Error: ${method} ${url}`, 'API', error);
  }

  /**
   * Log performance metrics
   */
  logPerformance(operation: string, duration: number, context?: string): void {
    if (duration > 1000) {
      this.warn(`Slow operation: ${operation} took ${duration}ms`, context || 'Performance');
    } else if (this.isDevelopment) {
      this.debug(`Operation: ${operation} took ${duration}ms`, context || 'Performance');
    }
  }

  /**
   * Create a timer for performance logging
   */
  startTimer(operation: string, context?: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.logPerformance(operation, duration, context);
    };
  }

  /**
   * Log user actions (useful for analytics)
   */
  logUserAction(action: string, details?: any): void {
    if (this.isDevelopment) {
      this.info(`User Action: ${action}`, 'UserAction', details);
    }
  }

  /**
   * Log authentication events
   */
  logAuth(event: string, details?: any): void {
    this.info(`Auth Event: ${event}`, 'Auth', details);
  }

  /**
   * Log navigation events
   */
  logNavigation(from: string, to: string, details?: any): void {
    if (this.isDevelopment) {
      this.debug(`Navigation: ${from} → ${to}`, 'Navigation', details);
    }
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export convenience functions
export const logDebug = (message: string, context?: string, data?: any) => 
  logger.debug(message, context, data);

export const logInfo = (message: string, context?: string, data?: any) => 
  logger.info(message, context, data);

export const logWarn = (message: string, context?: string, data?: any) => 
  logger.warn(message, context, data);

export const logError = (message: string, context?: string, error?: Error | any, data?: any) => 
  logger.error(message, context, error, data);

// Export default for easy import
export default logger;